const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const roundToTwoDecimals = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

const formatToPluralOrSingular = (count: number, text: string): string => {
  return count === 1 ? text : `${text}s`;
};

function formatDuration(milliseconds: number) {
  const totalSeconds = milliseconds / 1000;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes && seconds) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} and ${seconds} seconds`;
  } else if (minutes) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    return `${seconds}s`;
  }
}

const formatMeetingDuration = (durationMinutes: number): string => {
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (hours === 0) {
    return `${minutes}min`;
  } else if (hours === 1 && minutes === 0) {
    return '1hr';
  } else if (minutes === 0) {
    return `${hours}hr`;
  } else {
    return `${hours}hr ${minutes}min`;
  }
};

const formatMediaDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const paddedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const paddedSeconds = secs < 10 ? `0${secs}` : `${secs}`;
  return `${paddedMinutes}:${paddedSeconds}`;
};

const sanitizeObject = <T>(obj: any): T => {
  const sanitizedObj = { ...obj };
  Object.keys(sanitizedObj).forEach((key) => {
    if (sanitizedObj[key] === null || sanitizedObj[key] === undefined) {
      delete sanitizedObj[key];
    }
  });

  return sanitizedObj;
};

const removeSubString = (str: string, subString: string): string => {
  return str.replace(subString, '').trim();
};

const getSearchParamsFroUrl = (url: string): Record<string, string> => {
  if (!url.includes('?')) {
    return {};
  }
  const [, searchParams] = url.split('?');

  const params = searchParams.split('&').reduce((acc, param) => {
    const [key, value] = param.split('=');
    acc[key] = value;
    return acc;
  }, {} as Record<string, string>);

  return params;
};

const appendSearchParamsToUrl = (
  url: string,
  params: Record<string, string>
): string => {
  const searchParams = new URLSearchParams(params).toString();
  return `${url}?${searchParams}`;
};

/**
 * Composes a display name out of whichever parts are actually there. Both parts
 * are nullable on every candidate-shaped record, so the parts are filtered
 * before they are joined — interpolating them instead would render "Jane null"
 * or a bare space. `fallback` covers the records that have neither part.
 */
const getFullName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback = 'They'
): string => {
  const fullName = [firstName, lastName]
    .map((namePart) => namePart?.trim() ?? '')
    .filter((namePart) => namePart.length > 0)
    .join(' ');

  return fullName || fallback;
};

const removeFalsyValues = (obj: Record<string, any>): Record<string, any> => {
  return Object.keys(obj).reduce((acc, key) => {
    if (obj[key]) {
      acc[key] = obj[key];
    }
    return acc;
  }, {} as Record<string, any>);
};

const formatSearchParamValue = (value: string): string | boolean => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return value;
};

export {
  capitalizeFirstLetter,
  formatSearchParamValue,
  roundToTwoDecimals,
  formatToPluralOrSingular,
  formatDuration,
  sanitizeObject,
  removeSubString,
  getSearchParamsFroUrl,
  appendSearchParamsToUrl,
  getFullName,
  removeFalsyValues,
  formatMediaDuration,
  formatMeetingDuration
};
